'use client';

import React, { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePathname, useRouter } from 'next/navigation';
import {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
} from '@/components/ui/command';
import { useMediaQuery } from '@/hooks/use-media-query';

const emptySearchAction: 'none' | 'redirect' = 'redirect';

const SearchBar: React.FC = () => {
  const router = useRouter();
  const pathname = usePathname();
  const isXL = useMediaQuery('(min-width: 1280px)');

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');

  // Clear input and close dialog on route change
  useEffect(() => {
    setInput('');
    setOpen(false);
  }, [pathname]);

  // Close mobile dialog if the screen expands
  useEffect(() => {
    if (isXL && open) {
      setOpen(false);
    }
  }, [isXL, open]);

  const handleSearch = (query: string) => {
    const trimmed = query.trim();

    if (!trimmed) {
      if (emptySearchAction === 'redirect') {
        router.push('/');
      }
      return;
    }

    router.push(`/search?query=${encodeURIComponent(trimmed)}`);
    setOpen(false);
  };

  const handleDesktopKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch(e.currentTarget.value);
    }
  };

  const handleMobileKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch(input);
    }
  };

  return (
    <>
      {/* 🌐 Full Search Bar for xl and up */}
      <div
        className="group focus-within:ring-ring/50 focus-visible:border-primary hidden size-12 w-full items-center
          rounded-sm shadow-none focus-within:ring-3 xl:flex">
        <div
          className="group dark:bg-input/30 dark:focus-within:border-primary/30 focus-within:border-primary/40 flex
            size-12 w-full items-center rounded-sm border px-6 shadow-none transition-colors">
          <Search className="text-muted-foreground h-5 w-5" />
          <Input
            type="text"
            placeholder="Search..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleDesktopKeyDown}
            className="text-foreground placeholder:text-muted-foreground mx-0 h-6 w-full translate-px rounded-sm
              border-0 bg-transparent py-0 text-sm font-normal shadow-none placeholder:text-sm focus:ring-0
              focus:outline-none focus-visible:ring-0 dark:bg-transparent dark:focus:ring-0
              dark:focus-visible:ring-0"
          />
          <div className="bg-secondary text-secondary-foreground flex items-center rounded-sm px-1.5 py-0.5 text-xs">
            <span className="mr-0.5">⌘</span>
            <span>K</span>
          </div>
        </div>
      </div>

      {/* 📱 Icon only for < xl */}
      <div className="flex items-center justify-center xl:hidden">
        <Button
          size="default"
          variant="ghost"
          className="px-6 py-6"
          aria-label="Search"
          onClick={() => setOpen(true)}>
          <Search className="size-6 h-5 w-5" />
        </Button>
      </div>

      {/* 🧠 Command Dialog for Mobile */}
      <CommandDialog className="top-1/5" open={open} onOpenChange={setOpen}>
        <Command>
          <CommandInput
            autoFocus
            className="pr-10 pl-3"
            placeholder="Search products..."
            value={input}
            onValueChange={setInput}
            onKeyDown={handleMobileKeyDown}
          />
          <CommandList>
            <CommandEmpty>No results. Press enter to search.</CommandEmpty>
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  );
};

export default SearchBar;
